import {
  GetOrganizationCreationOptions,
  PostOrganization,
  PutOrganization,
  PostOrganizationCertificate,
  PostOrganizationBusinessPremise,
  GetOrganizationBusinessPremise,
  GetOrganizationElectronicDevice,
  PostOrganizationElectronicDevice,
  GetOrganizationSetupProgress,
  GetOrganization,
  GetOrganizationInvoices,
  PostOrganizationInvoice,
  GetOrganizationInvoice,
  PostSendInvoiceEmail,
  PostCancelInvoice,
  DeleteOrganizationBusinessPremise,
  PutOrganizationElectronicDevice,
  PutOrganizationBusinessPremise,
  GetCashRegisterStatus,
  PostCashRegisterClosure,
  GetCashRegisterClosures,
  GetCashRegisterClosure,
  DeleteCashRegisterClosure,
  PutOrganizationInvoicePrintCount,
  PutInvoicePaymentMethod,
  GetCashRegisterSettings,
  PutCashRegisterSettings,
  GetOrganizationBusinessPremiseAccountingExport,
  GetCashRegisterHasClosedYesterday,
  GetDownloadInvoice,
  GetOrganizationEstimateToInvoiceData,
  GetTaxRegisterSettings,
  GetTaxRegisterFiscalizationSettings,
  GetTaxRegisterFiscalizations,
  PutTaxRegisterFiscalizationSettings,
  PostSendCashRegisterClosure,
  GetOrganizationEmailAddress,
  PostOrganizationEmailAddress,
  DeleteOrganizationEmailAddress,
  PostResendOrganizationEmailAddressVerification,
  GetAutomaticInvoiceIssuingSettings,
  GetAutomaticInvoiceIssuingElectronicDevices,
  PutAutomaticInvoiceIssuingSetting,
  DeleteAutomaticInvoiceIssuingSetting,
  PutOrganizationDocumentSettings,
  PostOrganizationDocumentImage,
  GetOrganizationDocumentImage,
  DeleteOrganizationDocumentImage,
} from "@/server-types";
import { del, get, post, put } from "../request-helpers";
import { useCustomMutation } from "../custom-mutation";
import { useCustomQuery } from "../custom-query";
import { queryClient } from "..";

export const useGetCashRegisterHasClosedYesterday = ({
  businessPremiseId,
  disabled,
}: {
  businessPremiseId: string;
  disabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: ["getCashRegisterHasClosedYesterday", businessPremiseId],
    queryFn: () =>
      get<GetCashRegisterHasClosedYesterday>(
        `/tax-register/organization/business-premise/${businessPremiseId}/cash-register/has-closed-yesterday`,
      ),
    enabled: !disabled,
  });
};

export const useGetCashRegisterSettings = ({
  businessPremiseId,
}: {
  businessPremiseId: string;
}) => {
  return useCustomQuery({
    queryKey: ["getCashRegisterSettings", businessPremiseId],
    queryFn: () =>
      get<GetCashRegisterSettings>(
        `/tax-register/organization/business-premise/${businessPremiseId}/cash-register/settings`,
      ),
    enabled: !!businessPremiseId,
  });
};

export const usePutCashRegisterSettings = () => {
  return useCustomMutation({
    mutationFn: (
      body: PutCashRegisterSettings["body"] & {
        businessPremiseId: string;
      },
    ) =>
      put<PutCashRegisterSettings>(
        `/tax-register/organization/business-premise/${body.businessPremiseId}/cash-register/settings`,
        body,
      ),
  });
};

export const useGetOrganizationBusinessPremiseAccountingExport = () => {
  return useCustomMutation({
    mutationFn: ({
      businessPremiseId,
      query,
    }: {
      businessPremiseId: string;
      query: GetOrganizationBusinessPremiseAccountingExport["query"];
    }) =>
      get<GetOrganizationBusinessPremiseAccountingExport>(
        `/tax-register/organization/business-premise/${businessPremiseId}/accounting/export`,
        query,
      ),
  });
};

export const usePutInvoicePaymentMethod = () => {
  return useCustomMutation({
    mutationFn: ({
      invoiceId,
      body,
    }: {
      invoiceId: string;
      body: PutInvoicePaymentMethod["body"];
    }) =>
      put<PutInvoicePaymentMethod>(
        `/tax-register/organization/invoice/${invoiceId}/change-payment-method`,
        body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscription-invoices"] });
      queryClient.invalidateQueries({ queryKey: ["customer-subscription"] });
      queryClient.invalidateQueries({
        queryKey: ["customer-subscription-by-id"],
      });
      queryClient.invalidateQueries({
        queryKey: ["customer-subscription-events"],
      });
    },
  });
};

export const usePutOrganizationInvoicePrintCount = () => {
  return useCustomMutation({
    mutationFn: (invoiceId: string | undefined) =>
      put<PutOrganizationInvoicePrintCount>(
        `/tax-register/organization/invoice/${invoiceId}/print-count`,
      ),
  });
};

export const useGetCashRegisterClosures = ({
  businessPremiseId,
  query,
  disabled,
}: {
  businessPremiseId: string;
  query?: GetCashRegisterClosures["query"];
  disabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: ["cashRegisterClosures", businessPremiseId, query],
    queryFn: () =>
      get<GetCashRegisterClosures>(
        `/tax-register/organization/business-premise/${businessPremiseId}/cash-register/close`,
        query,
      ),
    enabled: !disabled,
  });
};

export const useDeleteCashRegisterClosure = ({
  businessPremiseId,
}: {
  businessPremiseId: string;
}) => {
  return useCustomMutation({
    mutationFn: (closureId: number) =>
      del<DeleteCashRegisterClosure>(
        `/tax-register/organization/business-premise/${businessPremiseId}/cash-register/close/${closureId}`,
      ),
  });
};

export const useGetCashRegisterClosure = ({
  businessPremiseId,
  closureId,
  query,
}: {
  businessPremiseId: string;
  closureId?: number;
  query?: GetCashRegisterClosure["query"];
}) => {
  return useCustomQuery({
    queryKey: ["cashRegisterClosure", businessPremiseId, closureId, query],
    queryFn: () =>
      get<GetCashRegisterClosure>(
        `/tax-register/organization/business-premise/${businessPremiseId}/cash-register/close/${closureId!}`,
        query,
      ),
    enabled: closureId != null,
  });
};

export const usePostCashRegisterClosure = () => {
  return useCustomMutation({
    mutationFn: ({
      businessPremiseId,
      body,
    }: {
      businessPremiseId: string;
      body: PostCashRegisterClosure["body"];
    }) =>
      post<PostCashRegisterClosure>(
        `/tax-register/organization/business-premise/${businessPremiseId}/cash-register/close`,
        body,
      ),
  });
};

export const useGetCashRegisterStatus = ({
  businessPremiseId,
  query,
  disabled,
}: {
  businessPremiseId: string;
  query?: GetCashRegisterStatus["query"];
  disabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: ["cashRegisterStatus", businessPremiseId, query],
    queryFn: () =>
      get<GetCashRegisterStatus>(
        `/tax-register/organization/business-premise/${businessPremiseId}/cash-register/status`,
        query,
      ),
    enabled: !disabled,
  });
};

export const useUpdateOrganization = () => {
  return useCustomMutation({
    mutationFn: ({ body }: { body: PutOrganization["body"] }) =>
      put<PutOrganization>("/tax-register/organization", body),
  });
};

export const useUpdateDocumentSettings = (options?: {
  onSuccess?: () => void;
  displayNotification?: boolean;
}) => {
  return useCustomMutation({
    displayNotification: options?.displayNotification,
    mutationFn: (body: PutOrganizationDocumentSettings["body"]) =>
      put<PutOrganizationDocumentSettings>(
        "/tax-register/organization/document-settings",
        body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getOrganization"] });
      options?.onSuccess?.();
    },
  });
};

export const useUploadOrganizationDocumentImage = (options?: {
  onSuccess?: () => void;
  displayNotification?: boolean;
}) => {
  return useCustomMutation({
    displayNotification: options?.displayNotification,
    mutationFn: ({
      type,
      file,
    }: {
      type: PostOrganizationDocumentImage["query"]["type"];
      file: File;
    }) => {
      const formData = new FormData();
      formData.append("file", file);
      return post<PostOrganizationDocumentImage>(
        "/tax-register/organization/document-image",
        formData,
        { type },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getOrganization"] });
      queryClient.invalidateQueries({
        queryKey: ["getOrganizationDocumentImage"],
      });
      options?.onSuccess?.();
    },
  });
};

export const useGetOrganizationDocumentImage = ({
  type,
  enabled,
}: {
  type: GetOrganizationDocumentImage["query"]["type"];
  enabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: ["getOrganizationDocumentImage", type],
    queryFn: () =>
      get<GetOrganizationDocumentImage>(
        "/tax-register/organization/document-image",
        { type },
      ),
    enabled: enabled ?? true,
  });
};

export const useDeleteOrganizationDocumentImage = (options?: {
  onSuccess?: () => void;
  displayNotification?: boolean;
}) => {
  return useCustomMutation({
    displayNotification: options?.displayNotification,
    mutationFn: ({
      type,
    }: {
      type: DeleteOrganizationDocumentImage["query"]["type"];
    }) =>
      del<DeleteOrganizationDocumentImage>(
        "/tax-register/organization/document-image",
        undefined,
        { type },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getOrganizationDocumentImage"],
      });
      options?.onSuccess?.();
    },
  });
};

export const useUpdateBusinessPremise = () => {
  return useCustomMutation({
    mutationFn: ({
      businessPremiseId,
      body,
    }: {
      businessPremiseId: string;
      body: PutOrganizationBusinessPremise["body"];
    }) =>
      put<PutOrganizationBusinessPremise>(
        `/tax-register/organization/business-premise/${businessPremiseId}`,
        body,
      ),
  });
};

export const useUpdateElectronicDevice = () => {
  return useCustomMutation({
    mutationFn: ({
      electronicDeviceId,
      body,
    }: {
      electronicDeviceId: string;
      body: PutOrganizationElectronicDevice["body"];
    }) =>
      put<PutOrganizationElectronicDevice>(
        `/tax-register/organization/electronic-device/${electronicDeviceId}`,
        body,
      ),
  });
};

export const useDeleteBusinessPremise = () => {
  return useCustomMutation({
    mutationFn: ({ businessPremiseId }: { businessPremiseId: string }) =>
      del<DeleteOrganizationBusinessPremise>(
        `/tax-register/organization/business-premise/${businessPremiseId}`,
      ),
  });
};

export const usePostCancelInvoice = () => {
  return useCustomMutation({
    mutationFn: ({
      invoiceId,
      body,
    }: {
      invoiceId: string;
      body: PostCancelInvoice["body"];
    }) =>
      post<PostCancelInvoice>(
        `/tax-register/organization/invoice/${invoiceId}/cancel`,
        body,
      ),
    onSuccess: async () => {
      // The canceled document keeps its id, so nothing refetches on its own.
      // Awaited, so the mutation stays pending until the canceled state is on
      // screen rather than settling over the document as it was.
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["getOrganizationInvoice"],
        }),
        queryClient.invalidateQueries({
          queryKey: ["getOrganizationInvoices"],
        }),
      ]);
      queryClient.invalidateQueries({ queryKey: ["subscription-invoices"] });
      queryClient.invalidateQueries({ queryKey: ["customer-subscription"] });
      queryClient.invalidateQueries({
        queryKey: ["customer-subscription-by-id"],
      });
      queryClient.invalidateQueries({
        queryKey: ["customer-subscription-events"],
      });
    },
  });
};

export const useGetDownloadInvoice = () => {
  return useCustomMutation({
    mutationFn: ({ invoiceId }: { invoiceId: string }) =>
      get<GetDownloadInvoice>(
        `/tax-register/organization/invoice/${invoiceId}/download`,
      ),
  });
};

/**
 * POST /tax-register/invoice/{invoiceId}/send
 */
export const usePostSendInvoiceEmail = () => {
  return useCustomMutation({
    mutationFn: ({
      invoiceId,
      body,
    }: {
      invoiceId: string;
      body: PostSendInvoiceEmail["body"];
    }) =>
      post<PostSendInvoiceEmail>(
        `/tax-register/organization/invoice/${invoiceId}/send-email`,
        body,
      ),
  });
};

/**
 * GET /tax-register/organization/invoice/{invoiceId}
 */
export const useGetOrganizationInvoice = (
  data: Omit<GetOrganizationInvoice["params"], "invoiceId"> & {
    invoiceId: string | undefined;
  },
) => {
  return useCustomQuery({
    queryKey: ["getOrganizationInvoice", data],
    queryFn: () =>
      get<GetOrganizationInvoice>(
        `/tax-register/organization/invoice/${data.invoiceId}`,
        data,
      ),
    enabled: data.invoiceId != undefined,
  });
};

/**
 * GET /tax-register/organization/invoice
 */
export const useGetOrganizationInvoices = (
  query: GetOrganizationInvoices["query"] & {
    disabled?: boolean;
  },
) => {
  return useCustomQuery({
    queryKey: ["getOrganizationInvoices", query],
    queryFn: () =>
      get<GetOrganizationInvoices>(`/tax-register/organization/invoice`, query),
    enabled: !query?.disabled,
  });
};

/**
 * POST /tax-register/organization/invoice
 */
export const usePostOrganizationInvoice = () => {
  return useCustomMutation({
    mutationFn: (body: PostOrganizationInvoice["body"]) =>
      post<PostOrganizationInvoice>(`/tax-register/organization/invoice`, body),
  });
};

export const useGetOrganizationEstimateToInvoiceData = ({
  invoiceId,
  enabled,
}: {
  invoiceId?: string;
  enabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: ["getOrganizationEstimateToInvoiceData", invoiceId],
    queryFn: () =>
      get<GetOrganizationEstimateToInvoiceData>(
        `/tax-register/organization/invoice/${invoiceId}/estimate-to-invoice-data`,
      ),
    enabled: invoiceId != null && enabled,
  });
};

/**
 * GET /tax-register/organization
 */
export const useGetOrganization = ({ disabled }: { disabled?: boolean }) => {
  return useCustomQuery({
    queryKey: ["getOrganization", disabled],
    queryFn: () => get<GetOrganization>("/tax-register/organization"),
    retry: false,
    enabled: !disabled,
  });
};

/**
 * GET /tax-register/organization/creation-options
 */
export const useGetOrganizationCreationOptions = ({
  disabled,
}: {
  disabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: ["getOrganizationCreationOptions"],
    queryFn: () =>
      get<GetOrganizationCreationOptions>(
        "/tax-register/organization/creation-options",
      ),
    enabled: !disabled,
  });
};

/**
 * GET /tax-register/organization/setup-progress
 */
export const useGetOrganizationSetupProgress = (disabled?: boolean) => {
  return useCustomQuery({
    queryKey: ["getOrganizationSetupProgress", disabled],
    queryFn: () =>
      get<GetOrganizationSetupProgress>(
        "/tax-register/organization/setup-progress",
      ),
    // placeholderData: keepPreviousData,
    enabled: !disabled,
  });
};

export const useGetOrganizationSetupProgressMutation = () => {
  return useCustomMutation({
    mutationFn: () =>
      get<GetOrganizationSetupProgress>(
        "/tax-register/organization/setup-progress",
      ),
  });
};

/**
 * POST /tax-register/organization
 */
export const usePostCreateOrganization = () => {
  return useCustomMutation({
    mutationFn: (body: PostOrganization["body"]) =>
      post<PostOrganization>("/tax-register/organization", body),
  });
};

/**
 * POST /tax-register/organization/{organizationId}/certificate
 */
export const usePostOrganizationCertificate = () => {
  return useCustomMutation({
    mutationFn: (body: FormData) =>
      post<PostOrganizationCertificate>(
        `/tax-register/organization/certificate`,
        body as unknown as { certificate: string; password: string }, // no bueno ampak bueno,
      ),
  });
};

/**
 * POST /tax-register/organization/businessPremise
 */
export const usePostOrganizationBusinessPremise = () => {
  return useCustomMutation({
    mutationFn: (body: PostOrganizationBusinessPremise["body"]) =>
      post<PostOrganizationBusinessPremise>(
        `/tax-register/organization/business-premise`,
        body,
      ),
    mutationKey: ["postOrganizationBusinessPremise"],
  });
};

/**
 * POST /tax-register/organization/businessPremise/search
 */
export const useGetOrganizationBusinessPremise = ({
  query,
  disabled,
}: {
  query?: GetOrganizationBusinessPremise["body"];
  disabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: ["getOrganizationBusinessPremise", query],
    queryFn: () =>
      post<GetOrganizationBusinessPremise>(
        `/tax-register/organization/business-premise/search`,
        query ?? {},
      ),
    enabled: !disabled,
  });
};

/**
 * GET /tax-register/organization/electronic-device
 */
export const useGetOrganizationElectronicDevice = (
  query: GetOrganizationElectronicDevice["query"] & {
    enabled?: boolean;
  },
) => {
  return useCustomQuery({
    queryKey: ["getOrganizationElectronicDevice", query],
    queryFn: () =>
      get<GetOrganizationElectronicDevice>(
        `/tax-register/organization/electronic-device`,
        query,
      ),
    enabled: query?.enabled,
  });
};

/**
 * POST /tax-register/organization/electronic-device
 */
export const usePostOrganizationElectronicDevice = () => {
  return useCustomMutation({
    mutationFn: (body: PostOrganizationElectronicDevice["body"]) =>
      post<PostOrganizationElectronicDevice>(
        `/tax-register/organization/electronic-device`,
        body,
      ),
    mutationKey: ["postOrganizationElectronicDevice"],
  });
};

export const useGetTaxRegisterSettings = () => {
  return useCustomQuery({
    queryKey: ["getTaxRegisterSettings"],
    queryFn: () => get<GetTaxRegisterSettings>("/tax-register/settings"),
  });
};

export const useGetTaxRegisterFiscalizationSettings = (enabled?: boolean) => {
  return useCustomQuery({
    queryKey: ["getTaxRegisterFiscalizationSettings"],
    queryFn: () =>
      get<GetTaxRegisterFiscalizationSettings>(
        "/tax-register/organization/fiscalization-settings",
      ),
    enabled: enabled ?? true,
  });
};

export const useGetTaxRegisterFiscalizations = ({
  query,
  enabled,
}: {
  query?: GetTaxRegisterFiscalizations["query"];
  enabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: ["getTaxRegisterFiscalizations", query],
    queryFn: () =>
      get<GetTaxRegisterFiscalizations>(
        "/tax-register/fiscalization",
        query ?? {},
      ),
    enabled: enabled ?? true,
  });
};

export const usePutTaxRegisterFiscalizationSettings = () => {
  return useCustomMutation({
    mutationFn: (props: {
      body: PutTaxRegisterFiscalizationSettings["body"];
    }) =>
      put<PutTaxRegisterFiscalizationSettings>(
        "/tax-register/organization/fiscalization-settings",
        props.body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getTaxRegisterFiscalizationSettings"],
      });
    },
  });
};

export const usePostSendCashRegisterClosure = () => {
  return useCustomMutation({
    mutationFn: (props: {
      params: PostSendCashRegisterClosure["params"];
      body: PostSendCashRegisterClosure["body"];
    }) =>
      post<PostSendCashRegisterClosure>(
        `/tax-register/organization/business-premise/${props.params.businessPremiseId}/cash-register/close/${props.params.closeId}/send`,
        props.body,
      ),
  });
};

export const useGetOrganizationEmailAddress = (enabled?: boolean) => {
  return useCustomQuery({
    queryKey: ["useGetOrganizationEmailAddress"],
    queryFn: () =>
      get<GetOrganizationEmailAddress>(
        "/tax-register/organization/email-address",
      ),
    enabled: enabled ?? true,
  });
};

export const usePostOrganizationEmailAddress = () => {
  return useCustomMutation({
    mutationFn: (props: { body: PostOrganizationEmailAddress["body"] }) =>
      post<PostOrganizationEmailAddress>(
        "/tax-register/organization/email-address",
        props.body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["useGetOrganizationEmailAddress"],
      });
    },
  });
};

export const useDeleteOrganizationEmailAddress = () => {
  return useCustomMutation({
    mutationFn: () =>
      del<DeleteOrganizationEmailAddress>(
        "/tax-register/organization/email-address",
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["useGetOrganizationEmailAddress"],
      });
    },
  });
};

export const usePostResendOrganizationEmailAddressVerification = () => {
  return useCustomMutation({
    mutationFn: () =>
      post<PostResendOrganizationEmailAddressVerification>(
        "/tax-register/organization/email-address/resend-email-verification",
      ),
  });
};

export const useGetAutomaticInvoiceIssuingSettings = (enabled?: boolean) => {
  return useCustomQuery({
    queryKey: ["getAutomaticInvoiceIssuingSettings"],
    queryFn: () =>
      get<GetAutomaticInvoiceIssuingSettings>(
        "/tax-register/automatic-invoice-issuing",
      ),
    enabled: enabled ?? true,
  });
};

export const useGetAutomaticInvoiceIssuingElectronicDevices = ({
  locationId,
  organizationId,
  enabled,
}: {
  locationId: number;
  organizationId: string;
  enabled?: boolean;
}) => {
  return useCustomQuery({
    queryKey: [
      "getAutomaticInvoiceIssuingElectronicDevices",
      locationId,
      organizationId,
    ],
    queryFn: () =>
      get<GetAutomaticInvoiceIssuingElectronicDevices>(
        "/tax-register/automatic-invoice-issuing/electronic-devices",
        { locationId, organizationId },
      ),
    enabled: (enabled ?? true) && !!organizationId && locationId > 0,
  });
};

export const usePutAutomaticInvoiceIssuingSetting = () => {
  return useCustomMutation({
    mutationFn: (body: PutAutomaticInvoiceIssuingSetting["body"]) =>
      put<PutAutomaticInvoiceIssuingSetting>(
        "/tax-register/automatic-invoice-issuing",
        body,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getAutomaticInvoiceIssuingSettings"],
      });
    },
  });
};

export const useDeleteAutomaticInvoiceIssuingSetting = () => {
  return useCustomMutation({
    mutationFn: (query: DeleteAutomaticInvoiceIssuingSetting["query"]) =>
      del<DeleteAutomaticInvoiceIssuingSetting>(
        "/tax-register/automatic-invoice-issuing",
        undefined,
        query,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getAutomaticInvoiceIssuingSettings"],
      });
    },
  });
};
